import { useQueryClient } from '@tanstack/react-query';
import { useAdminDeleteFeatureTag, getAdminGetFeatureTagsQueryKey } from '@/api/auth/admin/admin';
import type { FeatureTagResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { logger } from '@/services/logger';

interface DeleteFeatureTagDialogProps {
  /** The feature tag to delete, or null when the dialog is closed. */
  featureTag: FeatureTagResponse | null;
  /** Whether the dialog is open. */
  open: boolean;
  /** Callback when the dialog open state changes. */
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation dialog for deleting a feature catalog tag. The server rejects deletion with a
 * conflict when the tag is still configured on an organization; that message is surfaced as a toast.
 */
export function DeleteFeatureTagDialog({
  featureTag,
  open,
  onOpenChange,
}: DeleteFeatureTagDialogProps) {
  const queryClient = useQueryClient();

  const { mutate: deleteFeatureTag, isPending: isDeleting } = useAdminDeleteFeatureTag({
    mutation: {
      onSuccess: () => {
        toast.success('Feature tag deleted');
        queryClient.invalidateQueries({
          queryKey: getAdminGetFeatureTagsQueryKey(),
        });
        onOpenChange(false);
      },
      onError: (error: any) => {
        logger.error(error, { context: 'DeleteFeatureTag', featureTagId: featureTag?.id });
        toast.error(
          'Failed to delete feature tag. ' + (error.response?.data?.detail || error.message)
        );
      },
    },
  });

  const handleDelete = () => {
    if (featureTag) {
      deleteFeatureTag({ featureTagId: featureTag.id });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Delete Feature Tag</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this feature tag? This action cannot be undone. Tags
            still configured on an organization cannot be deleted.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-muted-foreground">Feature Code:</p>
          <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm">
            {featureTag?.featureCode || 'N/A'}
          </code>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete Feature Tag'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
